/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */


import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListSubheader from "@mui/material/ListSubheader";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import colors from "qqq/assets/theme/base/colors";
import AdvancedQueryPreview from "qqq/components/query/AdvancedQueryPreview";
import QueryHistoryPreviewErrorBoundary from "qqq/components/query/QueryHistoryPreviewErrorBoundary";
import renderSortAndColumns from "qqq/components/query/QueryHistorySortAndColumns";
import {QueryHistoryEntry, QueryHistoryState} from "qqq/models/query/QueryHistory";
import RecordQueryView from "qqq/models/query/RecordQueryView";
import {formatQueryHistoryGroupLabel, formatQueryHistoryTimeLabel, formatQueryHistoryTooltip} from "qqq/utils/qqq/QueryHistoryTimeUtils";
import React, {useState} from "react";

interface QueryHistoryMenuProps
{
   tableMetaData: QTableMetaData;
   state: QueryHistoryState;
   onRestore: (entry: QueryHistoryEntry) => void;

   /////////////////////////////////////////////////////////////////////////
   // this renders as an item inside the Views menu - restoring an entry  //
   // closes that menu too, since the user is done with it at that point. //
   /////////////////////////////////////////////////////////////////////////
   closeParentMenu: () => void;
}

const noOperation = () =>
{
};


/*******************************************************************************
 ** the text for line 1 of a row, saying where the query came from.
 *******************************************************************************/
function renderSourceLabel(entry: QueryHistoryEntry): JSX.Element
{
   const tagStyle = {ml: "0.5rem", fontSize: "0.75rem", color: colors.gray.main};

   const titleStyle = {fontWeight: 500};
   if (entry.source === "savedView")
   {
      return (<>
         <Box component="span" sx={titleStyle}>{entry.savedViewLabel ?? "Saved view"}</Box>
         {entry.savedViewModified && <Box component="span" sx={tagStyle}>modified</Box>}
      </>);
   }

   ////////////////////////////////////////////////////////////////////////////
   // unsaved views and links get plain labels - a saved view's name is what //
   // should stand out down the list                                         //
   ////////////////////////////////////////////////////////////////////////////
   return (<Box component="span">{entry.source === "url" ? "From a link" : "Unsaved View"}</Box>);
}


/*******************************************************************************
 ** build the view for an entry, or null if its stored json can't be turned into one.
 *******************************************************************************/
function buildViewOrNull(entry: QueryHistoryEntry): RecordQueryView | null
{
   try
   {
      return (RecordQueryView.buildFromJSON(entry.viewJson));
   }
   catch (e)
   {
      console.log("Error building a view for a query history entry", e);
      return (null);
   }
}


/*******************************************************************************
 ** a History item (for the Views menu) that opens a submenu of past queries,
 ** newest first, grouped by day.
 *******************************************************************************/
export default function QueryHistoryMenu({tableMetaData, state, onRestore, closeParentMenu}: QueryHistoryMenuProps): JSX.Element
{
   const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);

   const isEmpty = state.entries.length == 0;

   /*******************************************************************************
    **
    *******************************************************************************/
   function handleRowClick(entry: QueryHistoryEntry)
   {
      setAnchorElement(null);
      closeParentMenu();
      onRestore(entry);
   }

   /*******************************************************************************
    **
    *******************************************************************************/
   function renderRow(entry: QueryHistoryEntry, now: number): JSX.Element
   {
      const view = buildViewOrNull(entry);
      const hasCriteria = (view?.queryFilter?.criteria?.length ?? 0) > 0 || (view?.queryFilter?.subFilters?.length ?? 0) > 0;
      const isCurrent = entry.id === state.currentEntryId;

      //////////////////////////////////////////////////////////////////////////////////////
      // a criterion pill doesn't wrap, so a long one (e.g., a long "is any of" list)     //
      // would run out past the preview's rounded box - so the preview clips at its edge. //
      //////////////////////////////////////////////////////////////////////////////////////
      return (
         <MenuItem
            key={entry.id}
            component="li"
            className={isCurrent ? "queryHistoryEntry current" : "queryHistoryEntry"}
            data-source={entry.source}
            data-entry-id={entry.id}
            selected={isCurrent}
            onClick={() => handleRowClick(entry)}
            sx={{display: "block", whiteSpace: "normal", width: "420px", maxWidth: "100%", py: "0.5rem"}}
         >
            <Box display="flex" justifyContent="space-between" alignItems="baseline">
               <Box>{renderSourceLabel(entry)}</Box>
               <Tooltip title={formatQueryHistoryTooltip(entry.firstUsedAt, entry.lastUsedAt)} placement="right" componentsProps={{tooltip: {sx: {whiteSpace: "pre-line"}}}}>
                  <Box component="span" sx={{ml: "1rem", fontSize: "0.75rem", color: colors.gray.main, whiteSpace: "nowrap"}}>
                     {formatQueryHistoryTimeLabel(entry.lastUsedAt, now)}
                  </Box>
               </Tooltip>
            </Box>
            <Box sx={{fontSize: "0.875rem", "& .advancedQueryString": {overflow: "hidden"}}}>
               {
                  hasCriteria
                     ? <QueryHistoryPreviewErrorBoundary><AdvancedQueryPreview tableMetaData={tableMetaData} queryFilter={view.queryFilter} isEditable={false} isQueryTooComplex={false} removeCriteriaByIndexCallback={noOperation} /></QueryHistoryPreviewErrorBoundary>
                     : <Box sx={{border: `1px solid ${colors.grayLines.main}`, borderRadius: "0.75rem", background: "#FFFFFF", p: "0.5rem", minHeight: "2.5rem", display: "flex", alignItems: "center"}}><i>No filter</i></Box>
               }
            </Box>
            <Box sx={{fontSize: "0.75rem", color: colors.gray.main}}>
               {renderSortAndColumns(view, tableMetaData)}
            </Box>
         </MenuItem>
      );
   }

   /*******************************************************************************
    ** Menu does not accept fragments as children, so build one flat list of
    ** group headings and rows.  Only built while the menu is open, since parsing every
    ** entry is wasted work on the screen's many re-renders - and this takes "now" at open time.
    *******************************************************************************/
   function buildMenuChildren(): JSX.Element[]
   {
      if (anchorElement == null)
      {
         return ([]);
      }

      const now = Date.now();
      const children: JSX.Element[] = [];
      let previousGroupLabel: string | null = null;

      for (const entry of state.entries)
      {
         const groupLabel = formatQueryHistoryGroupLabel(entry.lastUsedAt, now);
         if (groupLabel !== previousGroupLabel)
         {
            ///////////////////////////////////////////////////////////////////////////////////////
            // headings stick to the top while the rows scroll under them - so they need an      //
            // opaque background, and to stack above the rows' pills.  The menu's paper is what  //
            // scrolls (so its scrollbar sits at its edge, as in other menus), and it has 0.5rem //
            // of padding - a heading stuck at top 0 would leave that band above it, with rows   //
            // showing through, so it sticks 0.5rem higher, at the paper's own top edge.         //
            ///////////////////////////////////////////////////////////////////////////////////////
            children.push(<ListSubheader key={`group-${entry.id}`} className="queryHistoryGroupLabel" sx={{background: "#FFFFFF", zIndex: 2, top: "-0.5rem"}}>{groupLabel}</ListSubheader>);
            previousGroupLabel = groupLabel;
         }

         children.push(renderRow(entry, now));
      }

      return (children);
   }

   ////////////////////////////////////////////////////////////////////////////////
   // the submenu is a sibling of its trigger, not a child - React events bubble //
   // through portals, so a row click inside a child would also re-open it.      //
   ////////////////////////////////////////////////////////////////////////////////

   /////////////////////////////////////////////////////////////////////////////////////
   // key presses inside the submenu would otherwise also reach the Views menu's list //
   // (React events bubble through portals), which moves focus a second time on every //
   // arrow key.  This is on the menu's root, so it runs after the menu has handled   //
   // Escape and Tab itself.                                                          //
   /////////////////////////////////////////////////////////////////////////////////////
   return (
      <>
         <MenuItem
            id="queryHistoryButton"
            disabled={isEmpty}
            onClick={(event) => setAnchorElement(event.currentTarget)}
         >
            <ListItemIcon><Icon>history</Icon></ListItemIcon>
            <Box flexGrow={1}>History</Box>
            <Icon sx={{ml: "1rem", color: colors.gray.main}}>chevron_right</Icon>
         </MenuItem>
         <Menu
            anchorEl={anchorElement}
            anchorOrigin={{vertical: "top", horizontal: "right"}}
            transformOrigin={{vertical: "top", horizontal: "left"}}
            open={Boolean(anchorElement) && !isEmpty}
            onClose={() => setAnchorElement(null)}
            onKeyDown={(event) => event.stopPropagation()}
            PaperProps={{style: {maxHeight: "calc(100vh - 200px)", minWidth: "300px", overflowY: "auto"}}}
         >
            {buildMenuChildren()}
         </Menu>
      </>
   );
}
